<template>
    <v-row justify="center" class="pb-3">
        <v-col class="text-center col-12">
            <v-btn
                class="text-none px-4 rounded-xl"
                color="#f5f5f5"
                @click="initiate"
            >
                <v-icon>mdi-plus-thick</v-icon>
                Add debit card
            </v-btn>
        </v-col>
        <v-spacer />
        <v-dialog v-model="dialog" persistent max-width="450px">
            <v-card>
                <v-toolbar dark flat color="primary">
                    <v-card-title>
                        <span class="headline">
                            {{ showCVVInfo ? 'What\'s CVV?' : 'Add Debit Card' }}
                        </span>
                    </v-card-title>
                    <v-spacer />
                    <v-btn icon color="white" @click="close">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text class="py-0">
                    <v-container class="px-0">
                        <v-row v-if="!showCVVInfo">
                            <v-col cols="12" v-if="countryIsGhanaOrNigeria">
                                <v-alert
                                    dense
                                    dismissible
                                    color="primary"
                                    type="info"
                                    v-if="showDisclaimer"
                                >
                                    Only Mastercard <img class="card-logo" src="/images/payment-logos/mastercard.png" />
                                    <span v-if="!$store.state.user.can_post_offers">
                                        and Visa
                                        <img
                                            class="card-logo visa"
                                            src="/images/payment-logos/visa-white.png"
                                        >
                                    </span>
                                    bank cards can be added
                                </v-alert>
                                <v-autocomplete
                                    v-model="selectedBank"
                                    :error-messages="errors.first('bank')"
                                    v-validate="'required'"
                                    style="height: 3.75rem;"
                                    class="pb-0 pb-lg-3"
                                    @focus="showDisclaimer = true"
                                    aria-required
                                    required
                                    label="Bank"
                                    name="bank"
                                    :items="banks[$store.state.user.country_abbreviation] || []"
                                    item-text="name"
                                    return-object
                                    item-color="primary"
                                    color="primary"
                                    outlined
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-validate="'credit_card|required'"
                                    label="Card Number"
                                    outlined
                                    style="height: 3.75rem;"
                                    class="pb-0 pb-lg-3"
                                    name="card number"
                                    type="text"
                                    v-model="cardNumberInput"
                                    required
                                    :error-messages="errors.first('card number')"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    :items="months"
                                    v-model="expiryMonth"
                                    outlined
                                    style="height: 3.75rem;"
                                    class="pb-0 pb-lg-3"
                                    label="Month"
                                    name="month"
                                    v-validate="'required'"
                                    :error-messages="errors.first('month')"
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    :items="years"
                                    v-model="expiryYear"
                                    label="Year"
                                    outlined
                                    style="height: 3.75rem;"
                                    class="pb-0 pb-lg-3"
                                    name="year"
                                    v-validate="'required'"
                                    :error-messages="errors.first('month')"
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-validate="'min:3|max:4|required'"
                                    label="CVV"
                                    outlined
                                    style="height: 3.75rem;"
                                    class="pb-0 pb-lg-3"
                                    name="cvv"
                                    type="text"
                                    v-model="cvv"
                                    required
                                    :error-messages="errors.first('cvv')"
                                ></v-text-field>

                                <v-btn
                                    color="primary"
                                    v-if="!errors.first('cvv')"
                                    small
                                    text
                                    @click="showCVVInfo = true"
                                >
                                    What is CVV?
                                </v-btn>
                            </v-col>
                            <v-col cols="12" :class="{ 'mt-8': !!errors.first('cvv') }">
                                <v-alert
                                    dense
                                    dismissible
                                    class="mx-3"
                                    color="primary"
                                    type="info"
                                >
                                    A picture of the front of your debit card is
                                    needed to authenticate your card and prevent card fraud. Thank you
                                </v-alert>
                                <v-file-input
                                    v-model="image"
                                    v-validate="'required|image|size:2048'"
                                    accept="image/png, image/jpeg, image/bmp"
                                    prepend-icon="mdi-camera"
                                    label="Upload an image of just the front of your debit card"
                                    name="image"
                                    :error-messages="errors.first('image')"
                                    @change="onFileChange"
                                ></v-file-input>
                            </v-col>
                            <div class="mx-auto" v-if="url">
                                <v-img
                                    max-height="300"
                                    max-width="400"
                                    :src="url"
                                ></v-img>
                            </div>
                        </v-row>

                        <v-row v-else>
                            <v-col class="justify-center">
                                <v-img
                                    src="/images/cvv-card.jpg"
                                    height="200"
                                    max-width="300"
                                    class="mb-3 mx-auto"
                                />

                                <p>
                                    Card Verification Value or CVV is the last three digit at the back of your card
                                </p>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <template v-if="!showCVVInfo">
                        <v-btn color="primary" text @click="close">
                            Close
                        </v-btn>
                        <v-btn color="primary white" class="white--text" @click="save">
                            Save
                        </v-btn>
                    </template>
                    <v-btn v-else color="primary white" class="white--text" @click="showCVVInfo = false">
                        Ok
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <PaymentMethodAddedModal v-model="showPaymentMethodAddedModal" />
    </v-row>
</template>

<script>
    import { mapState } from 'vuex';
    import PaymentMethodAddedModal from './settings/payment-method-added-modal.vue';

    export default {
        components: {
            PaymentMethodAddedModal,
        },
        data() {
            return {
                showDisclaimer: false,
                showCVVInfo: false,
                dialog: false,
                cardNumber: null,
                expiryMonth: null,
                expiryYear: null,
                selectedBank: null,
                image: null,
                url: null,
                cvv: null,
                loadingAuthUrl: true,
                interval: null,
                banks: {
                    NG: [
                        {
                            code: '044',
                            name: 'Access Bank',
                        },
                        {
                            code: '011',
                            name: 'First Bank PLC',
                        },
                        {
                            code: '070',
                            name: 'Fidelity Bank',
                        },
                        {
                            code: '058',
                            name: 'Guaranty Trust Bank',
                        },
                        {
                            code: '076',
                            name: 'Polaris bank',
                        },
                        {
                            code: '221',
                            name: 'Stanbic IBTC Bank',
                        },
                        {
                            code: '232',
                            name: 'Sterling Bank PLC',
                        },
                        {
                            code: '033',
                            name: 'United Bank for Africa',
                        },
                        {
                            code: '032',
                            name: 'Union Bank PLC',
                        },
                        {
                            code: '035',
                            name: 'Wema Bank PLC',
                        },
                    ],
                    GH: [
                        { code: '20313500', name: 'ABSA (Barclays Bank of Ghana Limited)' },
                        { code: '26153374', name: 'Access Bank (Ghana) PLC' },
                        { code: '22031960', name: 'Cal Bank Limited' },
                        { code: '11350352', name: 'Consolidated Bank Ghana Limited' },
                        { code: 'GH130100', name: 'Ecobank Ghana' },
                        { code: '25570554', name: 'Fidelity Bank Ghana Ltd.' },
                        { code: '20313600', name: 'GCB Bank Limited' },
                        { code: '20314100', name: 'Societe Generale Ghana Limited' },
                        { code: '97151844', name: 'Stanbic Bank Ghana Limited' },
                        { code: '2006228LG0', name: 'United Bank for Africa (Ghana) Ltd.' },
                        { code: '20313800', name: 'Universal Merchant Bank' },
                        { code: '98357706', name: 'Zenith Bank (Ghana) Limited' },
                        { coe: '20313200', name: 'Agricultural Development Bank Limited' },
                        { coe: '20314200', name: 'Standard Chartered Ghana' },
                    ],
                },
                showPaymentMethodAddedModal: false,
            };
        },
        methods: {
            initiate() {
                this.$emit('click');
                const vm = this;
                this.$swal({
                    title: 'Heads Up!',
                    html: `
                    <div>
                        <img src="/images/warning.svg" height="200" />
                    </div>
                    <br />
                    <p class="disclaimer-text">
                        Ensure that your debit card is enabled for online transactions.
                        <br />
                        <br />
                        Most new debit cards are only permitted for ATM withdrawal and require you to contact your bank to upgrade them for online transaction use.
                        <br />
                        <br />
                        Thank you.
                    </p>
                `,
                    text: '',
                    preConfirm() {
                        vm.dialog = true;
                    },
                });
            },
            processCallbackData(data) {
                const {
                    type,
                    status,
                    message,
                } = data;

                if (type && type === 'chargeVerificationCompleted') {
                    const lowerCaseSatus = status.toLowerCase();
                    if (lowerCaseSatus === 'charge_verified' || lowerCaseSatus === 'approved') {
                        this.onSuccess(lowerCaseSatus);
                    } else {
                        this.onError(message || status);
                    }

                    this.loadingAuthUrl = false;
                    clearInterval(this.interval);
                }
            },
            resetLocalStorageListener() {
                clearInterval(this.interval);
                localStorage.setItem('chargeVerificationCallbackData', null);

                this.interval = setInterval(() => {
                    const stringifiedData = localStorage.getItem('chargeVerificationCallbackData');
                    try {
                        const callbackData = JSON.parse(stringifiedData);
                        console.log('callbackData', callbackData);
                        if (callbackData) {
                            this.processCallbackData(callbackData);
                        }
                    } catch (err) {
                        console.log(err);
                    }
                }, 100);
            },
            onFileChange(file) {
                const vm = this;
                if(!file) return vm.url = null;
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = function () {
                    vm.url = reader.result;
                };
                reader.onerror = function (error) {
                    console.log('Error: ', error);
                    vm.url = null;
                };
            },
            close() {
                this.dialog=false;
                this.cardNumber = null;
                this.expiryMonth = null;
                this.expiryYear = null;
                this.image = null;
                this.url = null;
                this.cvv = null;

                this.errors.clear();
            },
            save() {
                this.$validator.validateAll().then((isValid) => {
                    if (isValid) {
                        this.submit();
                    }
                });
            },
            submit() {
                const payload = {
                    number: this.cardNumber.replace(/ /g, ''),
                    expiry_month: this.expiryMonth,
                    expiry_year: this.expiryYear,
                    cvv: this.cvv,
                    bankName: this.selectedBank.name,
                    bankCode: this.selectedBank.code,
                };

                this.$emit('loading');
                Vue.backendApi().cardVerification()
                    .create(this.image, payload)
                    .then((response) => {
                        const {
                            status,
                            charge_response: chargeResponse,
                        } = response.data;

                        const lowerCaseSatus = status.toLowerCase();
                        if (status === 'charge_verified' || status === 'approved') {
                            this.onSuccess();
                            return;
                        }

                        const {
                            authModelUsed,
                            authurl,
                        } = chargeResponse.data;

                        if (authModelUsed === 'VBVSECURECODE') {
                            this.loadingAuthUrl = true;

                            this.resetLocalStorageListener();
                            this.popupCenter(authurl, '', 0.85, 0.85, () => {
                                this.$emit('loaded');
                                this.$swal({
                                    type: 'error',
                                    title: 'Your browser is blocking a pop-up window',
                                    html: `Turn off the pop up window blocker and try again.
                                  If unable to turn off pop up window blocker, copy the
                                  link below and open it in a new browser window and
                                  continue with your debit card validation.
                                  <div>
                                    <br>
                                    <strong>${authurl}</strong>
                                  </div>`,
                                    showCancelButton: true,
                                    cancelButtonText: 'Close',
                                    confirmButtonText: 'Copy',
                                    preConfirm() {
                                        navigator.clipboard.writeText(authurl).then(() => {
                                            alert('Copied to clipboard');
                                        }).catch((err) => {
                                            alert(err);
                                        });
                                        return false;
                                    },
                                });
                            });
                        }
                    })
                    .catch((err) => {
                        this.onError(err)
                    });
            },
            onSuccess(status) {
                this.$emit('loaded');
                if (status === 'approved') {
                    const html = `<ul style="margin: 0 30px; line-height: 1.5; text-align: left;">
                        <li style="margin-bottom: 10px;">
                            Now you can conduct transactions with this payment method on the Kaoshi network
                        </li>
                    </ul>`;

                    if (this.onboarding) {
                        this.showPaymentMethodAddedModal = true;
                    } else {
                        this.$swal({
                            type: 'success',
                            title: 'Congratulations!',
                            html,
                        });
                    }
                } else {
                    this.$swal({
                        type: 'success',
                        title: 'Thank You!',
                        text: 'Your debit card would be authenticated within the next 24 hours',
                    });
                }

                this.close();
            },
            onError(text) {
                this.$emit('loaded');

                this.$swal({
                    type: 'error',
                    title: text,
                    showCancelButton: false,
                    confirmButtonText: 'Pay with Bank Transfer',
                    customClass: 'verification-failed',
                    showCloseButton: true,
                }).then(({ value }) => {
                    if(value) {
                        this.$router.push({
                            name: 'offers-create',
                            params: {
                                payWithBankTransfer: true,
                            },
                        });
                    }
                });
            },
            range: (start, end) => Array(end - start + 1).fill().map((_, idx) => start + idx),
        },
        computed: {
            ...mapState([
                'onboarding',
            ]),
            currentMonth() {
                return (new Date()).getMonth() + 1;
            },
            currentYear() {
                return (new Date()).getFullYear();
            },
            months() {
                if (this.expiryYear === this.currentYear) {
                    return this.range(this.currentMonth, 12);
                }
                return this.range(1, 12);
            },
            years() {
                const year = new Date().getFullYear();
                return this.range(year, year + 11);
            },
            cardNumberInput: {
                get() {
                    return this.cardNumber;
                },
                set(newValue) {
                    const cardNumber = String(newValue).replace(/ /g, '');
                    const splittedNumbers = cardNumber.match(/.{1,4}/g);
                    this.cardNumber = (splittedNumbers || []).join(' ');
                }
            },
            countryIsGhanaOrNigeria() {
                const { country_abbreviation } = this.$store.state.user;
                return country_abbreviation === 'NG' || country_abbreviation === 'GH';
            }
        },
    }
</script>

<style scoped>
.v-file-input >>> .v-text-field__slot label {
    font-size: 0.85rem;
    max-width: 100%;
}

.card-logo {
    position: relative;
    top: 3px;
    height: 25px;
}

.card-logo.visa {
    height: 18px;
}

.v-toolbar {
    position: sticky;
    top: 0;
    z-index: 9;
}

@media screen and (max-width: 767px) {
    .v-file-input >>> .v-input__prepend-outer {
        margin-right: -23px;
    }

    .v-file-input >>> .v-text-field__slot label {
        font-size: 0.78rem;
        left: 25px !important;
    }
}
</style>

<style>
.verification-failed .swal2-title {
    font-size: 1.55rem !important;
    color: #1B1A1A !important;
}

.verification-failed .swal2-actions .swal2-confirm.swal2-styled:focus {
    box-shadow: none !important;
}

.verification-failed .swal2-close {
    color: #1B1A1A !important;
    top: 5px !important;
    right: 10px !important;
    width: 1em !important;
    height: 1em !important;
}
</style>
